import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular';

export type ToastType = 'success' | 'error' | 'warning' | 'info' | 'primary';

interface ToastConfig {
  color: string;
  icon: string;
}

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private readonly toastController = inject(ToastController);

  /**
   * Posición única para toasts en la app (cabecera / más visible en móvil).
   * Ionic usa `bottom` por defecto si no se indica `position`.
   */
  private readonly posicionPredeterminada = 'top' as const;

  /**
   * Determina el color e icono basado en el tipo de mensaje
   */
  private getToastConfig(type: ToastType): ToastConfig {
    const configs: Record<ToastType, ToastConfig> = {
      success: {
        color: 'success',
        icon: 'checkmark-circle-outline'
      },
      error: {
        color: 'danger',
        icon: 'close-circle-outline'
      },
      warning: {
        color: 'warning',
        icon: 'warning-outline'
      },
      info: {
        color: 'primary',
        icon: 'information-circle-outline'
      },
      primary: {
        color: 'primary',
        icon: 'key-outline'
      }
    };

    return configs[type];
  }

  /**
   * Detecta automáticamente el tipo de mensaje basado en palabras clave
   */
  private detectMessageType(mensaje: string, tipoExplicito?: ToastType): ToastType {
    if (tipoExplicito) {
      return tipoExplicito;
    }

    const mensajeLower = mensaje.toLowerCase();

    // Palabras clave para success
    if (
      mensajeLower.includes('correctamente') ||
      mensajeLower.includes('exitoso') ||
      mensajeLower.includes('guardado') ||
      mensajeLower.includes('guardada') ||
      mensajeLower.includes('enviado') ||
      mensajeLower.includes('enviada') ||
      mensajeLower.includes('descargado') ||
      mensajeLower.includes('descargada') ||
      mensajeLower.includes('bienvenido') ||
      mensajeLower.includes('iniciada') ||
      mensajeLower.includes('obtenida') ||
      mensajeLower.includes('sincronizado') ||
      mensajeLower.includes('completado')
    ) {
      return 'success';
    }

    // Palabras clave para error
    if (
      mensajeLower.includes('error') ||
      mensajeLower.includes('incorrecto') ||
      mensajeLower.includes('incorrectos') ||
      mensajeLower.includes('fallo') ||
      mensajeLower.includes('falló') ||
      mensajeLower.includes('no se pudo') ||
      mensajeLower.includes('no se encontró') ||
      mensajeLower.includes('no se guardó') ||
      mensajeLower.includes('no se actualizó')
    ) {
      return 'error';
    }

    // Palabras clave para warning
    if (
      mensajeLower.includes('advertencia') ||
      mensajeLower.includes('atención') ||
      mensajeLower.includes('expir') ||
      mensajeLower.includes('sincronizará') ||
      mensajeLower.includes('localmente') ||
      mensajeLower.includes('conectividad') ||
      mensajeLower.includes('no hay datos') ||
      mensajeLower.includes('próximamente') ||
      mensajeLower.includes('completa todos los campos')
    ) {
      return 'warning';
    }

    // Palabras clave para info/primary
    if (
      mensajeLower.includes('clave única') ||
      mensajeLower.includes('información') ||
      mensajeLower.includes('nota')
    ) {
      return 'info';
    }

    // Por defecto, usar info
    return 'info';
  }

  /**
   * Muestra un mensaje toast con color e icono automáticos.
   * La posición siempre es la parte superior de la pantalla.
   *
   * @param mensaje El mensaje a mostrar
   * @param tipo Tipo explícito del mensaje (opcional, se detecta automáticamente si no se proporciona)
   * @param duracion Duración en milisegundos (por defecto 2000)
   */
  async mostrar(mensaje: string, tipo?: ToastType, duracion: number = 2000): Promise<void> {
    const tipoDetectado = this.detectMessageType(mensaje, tipo);
    const config = this.getToastConfig(tipoDetectado);

    const toast = await this.toastController.create({
      message: mensaje,
      duration: duracion,
      position: this.posicionPredeterminada,
      color: config.color,
      icon: config.icon,
      buttons: [
        {
          text: 'OK',
          role: 'cancel'
        }
      ]
    });

    await toast.present();
  }

  /**
   * Muestra un mensaje de éxito
   */
  async mostrarExito(mensaje: string, duracion: number = 2000): Promise<void> {
    await this.mostrar(mensaje, 'success', duracion);
  }

  /**
   * Muestra un mensaje de error
   */
  async mostrarError(mensaje: string, duracion: number = 3000): Promise<void> {
    await this.mostrar(mensaje, 'error', duracion);
  }

  /**
   * Muestra un mensaje de advertencia
   */
  async mostrarAdvertencia(mensaje: string, duracion: number = 3000): Promise<void> {
    await this.mostrar(mensaje, 'warning', duracion);
  }

  /**
   * Muestra un mensaje informativo
   */
  async mostrarInfo(mensaje: string, duracion: number = 2000): Promise<void> {
    await this.mostrar(mensaje, 'info', duracion);
  }
}
